<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		>
			<template #heroContent>
				<div v-if="defaults[locale].homepage.headingTitle" class="hero-content">
					<span class="hero-subtitle">
						{{ defaults[locale].homepage.headingSubTitle }}
					</span>
					<span class="hero-title">
						{{ defaults[locale].homepage.headingTitle }}
					</span>
				</div>
			</template>
			<template #hero>
				<div class="hero-media">
					<iframe
						v-if="page.embedCode"
						class="hero-frame"
						:src="`${page.embedCode}?loop=1&muted=1&badge=0&autoplay=1&controls=0&autopause=0&player_id=0`"
						frameborder="0"
						allow="autoplay; fullscreen; picture-in-picture"
						allowfullscreen
						title="Metzlr House video"
					/>
					<picture>
						<source :srcset="page.headingImageWebp" type="image/webp" />
						<source :srcset="page.headingImage" />
						<img class="hero-frame slider-image" :src="page.headingImage" :alt="page.headingImageAlt" />
					</picture>
				</div>
			</template>
		</heading-image>

		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			:introduction-content="page.introductionContent"
		/>

		<booking-benefits
			:title="defaults[locale].homepage.sectionBenefitsTitle"
			:subtitle="defaults[locale].homepage.sectionBenefitsSubtitle"
			:benefits="defaults[locale].homepage.bookingBenefits"
			:button="defaults[locale].website.book"
			:image="{
				image: defaults[locale].homepage.sectionBenefitsImage,
				imageAlt: defaults[locale].homepage.sectionBenefitsImageAlt,
				imageWebp: defaults[locale].homepage.sectionBenefitsImageWebp,
			}"
		/>

		<gallery-slider :items="galleryHomepagedata" />

		<rooms-promotions
			:items="roomsData"
			:header="defaults[locale].rooms.sectionRoomsTitle"
			:subtitle="defaults[locale].rooms.subtitle"
			:content="defaults[locale].rooms.sectionRoomsContent"
			:button="defaults[locale].rooms.view"
		/>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:contentblocks="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>

		<promoblocks
			v-if="promoblocksData.length"
			:header="defaults[locale].homepage.sectionPromotionTitle"
			:subtitle="defaults[locale].homepage.sectionPromotionSubtitle"
			:promoblocks="promoblocksData"
			:amount="4"
		/>

		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>

		<news-overview v-if="news.length" :news="news" />
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:subtitle="page.frequentlyAskedQuestion.subtitle"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer
			v-if="page && page.footerSection && page.footerSection.length"
			:footer-section="page.footerSection[0]"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: promoblocksData } = await useWebsiteFetch('promoblocks', {
	query: { language: locale.value },
	key: `${locale.value}/promoblocks`,
});

const { data: galleryHomepagedata } = await useWebsiteFetch('galleryHomepage', {
	query: { language: locale.value },
	key: `${locale.value}/galleryHOmepage`,
});

const { data: news } = await useWebsiteFetch('news', {
	query: { language: locale.value },
	key: `${locale.value}/news`,
});

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
.video-background {
	background-image: url('~/assets/images/poster.jpg');
	background-size: cover;
}

.hero-media {
	position: absolute;
	inset: 0;
	container: hero-media / size;
	z-index: 2;

	.hero-frame {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 100%;
		height: auto;
		aspect-ratio: 16 / 9;
		transform: translate(-50%, -50%);
	}

	iframe {
		z-index: 1;
	}
}

@container hero-media (max-aspect-ratio: 16 / 9) {
	.hero-media .hero-frame {
		width: auto;
		height: 100%;
	}
}

.slider-image {
	object-fit: cover;
}

.hero-content {
	position: absolute;
	top: 45%;
	left: 0;
	right: 0;
	margin: 0 auto;
	z-index: 3;
	text-align: center;
	padding: 0 20px;
	color: #fff;
	max-width: 1060px;
	animation: fade-from-bottom 1.4s ease;

	span {
		color: #fff;
		font-weight: 200;
		font-size: 70px;
		letter-spacing: 1px;
		width: 100%;
		font-family: var(--heading-font-family);
		display: inline-block;
		margin: 0 0 10px;
	}

	.hero-subtitle {
		font-size: 26px;
		font-weight: 400;
		text-transform: uppercase;
		font-family: var(--body-font-family);
		letter-spacing: 2px;
	}
}

@media (max-width: 880px) {
	.hero-content {
		.hero-title {
			font-size: 62px;
		}

		.hero-subtitle {
			font-size: 24px;
		}
	}
}

@media (max-width: 680px) {
	.hero-content {
		top: 42%;

		.hero-title {
			font-size: 54px;
		}

		.hero-subtitle {
			font-size: 20px;
			margin: 0 0 15px;
		}
	}
}
</style>
